/**
 * Which StopReason values are legal to send for each OCPP version.
 *
 * These sets mirror the vendored OCA JSON schemas verbatim (1.6
 * StopTransaction.reason; 2.0.1 / 2.1 ReasonEnumType in TransactionEvent).
 * A unit test cross-checks them against the schema files so this map can
 * never silently drift from the source of truth (CLAUDE.md §4.5). The
 * adapter + Ajv remain the final backstop: an illegal value is rejected at
 * send time, this map just drives the version-aware reason picker.
 */
import { OcppVersion, StopReason } from './types';

/** OCPP 1.6J — StopTransaction.reason (11 values). */
export const STOP_REASONS_V16: readonly StopReason[] = [
  StopReason.EmergencyStop,
  StopReason.EVDisconnected,
  StopReason.HardReset,
  StopReason.Local,
  StopReason.Other,
  StopReason.PowerLoss,
  StopReason.Reboot,
  StopReason.Remote,
  StopReason.SoftReset,
  StopReason.UnlockCommand,
  StopReason.DeAuthorized,
];

/** OCPP 2.0.1 — ReasonEnumType (19 values). No HardReset/SoftReset/UnlockCommand. */
export const STOP_REASONS_V201: readonly StopReason[] = [
  StopReason.DeAuthorized,
  StopReason.EmergencyStop,
  StopReason.EnergyLimitReached,
  StopReason.EVDisconnected,
  StopReason.GroundFault,
  StopReason.ImmediateReset,
  StopReason.Local,
  StopReason.LocalOutOfCredit,
  StopReason.MasterPass,
  StopReason.Other,
  StopReason.OvercurrentFault,
  StopReason.PowerLoss,
  StopReason.PowerQuality,
  StopReason.Reboot,
  StopReason.Remote,
  StopReason.SOCLimitReached,
  StopReason.StoppedByEV,
  StopReason.TimeLimitReached,
  StopReason.Timeout,
];

/** OCPP 2.1 — ReasonEnumType (20 values): 2.0.1 set + ReqEnergyTransferRejected. */
export const STOP_REASONS_V21: readonly StopReason[] = [
  ...STOP_REASONS_V201,
  StopReason.ReqEnergyTransferRejected,
];

/** The stop reasons a station running `version` may legally send. */
export function stopReasonsForVersion(version: OcppVersion): readonly StopReason[] {
  switch (version) {
    case OcppVersion.V16J:
      return STOP_REASONS_V16;
    case OcppVersion.V201:
      return STOP_REASONS_V201;
    case OcppVersion.V21:
      return STOP_REASONS_V21;
  }
}
